import React, { useCallback, useState } from 'react';
import {
    Box,
    Checkbox,
    Chip,
    FormControlLabel,
    Paper,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { Security as SafetyIcon } from '@mui/icons-material';
import ConfirmationDialog from './ConfirmationDialog';
import ParamRow, { getParamEntry } from './ParamRow';

// Section / row definitions. Each row references one ArduPilot parameter.
// description is short, displayed as a tooltip on the param name.
const SECTIONS = [
    {
        title: 'Arming',
        rows: [
            { name: 'ARMING_CHECK', desc: 'Bitmask of pre-arm checks. 1 = all, 0 = none.' },
            { name: 'ARMING_REQUIRE', desc: 'Require arming before throttle (Plane).' },
            { name: 'ARMING_RUDDER', desc: 'Allow arm/disarm by rudder. 0 disable, 1 arm, 2 arm+disarm.' },
            { name: 'DISARM_DELAY', desc: 'Auto-disarm delay (s) after landing.' },
        ],
    },
    {
        title: 'Safety Switch',
        rows: [
            { name: 'BRD_SAFETYENABLE', desc: 'Enable hardware safety switch.' },
            { name: 'BRD_SAFETY_DEFLT', desc: 'Default state of safety switch at boot.' },
            { name: 'BRD_SAFETYOPTION', desc: 'Safety switch behavior bitmask.' },
            { name: 'BRD_SAFETY_MASK', desc: 'Channels still active when safety is on (Plane).' },
        ],
    },
    {
        title: 'Radio Failsafe',
        rows: [
            { name: 'FS_THR_ENABLE', desc: 'Throttle / radio failsafe action.' },
            { name: 'FS_THR_VALUE', desc: 'PWM below which throttle failsafe triggers.' },
            { name: 'THR_FAILSAFE', desc: 'Enable throttle failsafe (Plane).' },
            { name: 'THR_FS_VALUE', desc: 'Failsafe PWM threshold (Plane).' },
            { name: 'FS_SHORT_ACTN', desc: 'Short failsafe action (Plane).' },
            { name: 'FS_LONG_ACTN', desc: 'Long failsafe action (Plane).' },
        ],
    },
    {
        title: 'GCS / Link Failsafe',
        rows: [
            { name: 'FS_GCS_ENABLE', desc: 'GCS link loss failsafe (Copter/Rover).' },
            { name: 'FS_GCS_ENABL', desc: 'GCS link loss failsafe (Plane).' },
            { name: 'FS_GCS_TIMEOUT', desc: 'Timeout (s) before GCS failsafe triggers.' },
        ],
    },
    {
        title: 'EKF / Crash',
        rows: [
            { name: 'FS_EKF_ACTION', desc: 'Action when EKF variance is too high.' },
            { name: 'FS_EKF_THRESH', desc: 'EKF variance threshold.' },
            { name: 'FS_CRASH_CHECK', desc: 'Enable crash detection auto-disarm.' },
            { name: 'FS_VIBE_ENABLE', desc: 'Enable vibration failsafe.' },
        ],
    },
    {
        title: 'Battery Failsafe',
        rows: [
            { name: 'BATT_FS_LOW_ACT', desc: 'Action on low battery.' },
            { name: 'BATT_FS_CRT_ACT', desc: 'Action on critical battery.' },
            { name: 'BATT_LOW_VOLT', desc: 'Low battery voltage threshold.' },
            { name: 'BATT_LOW_MAH', desc: 'Low battery capacity threshold (mAh).' },
            { name: 'BATT_CRT_VOLT', desc: 'Critical battery voltage threshold.' },
            { name: 'BATT_CRT_MAH', desc: 'Critical battery capacity threshold (mAh).' },
            { name: 'BATT_FS_VOLTSRC', desc: 'Voltage source for failsafe (raw / sag-comp).' },
            { name: 'BATT_LOW_TIMER', desc: 'Seconds below threshold before failsafe.' },
        ],
    },
    {
        title: 'Geofence',
        rows: [
            { name: 'FENCE_ENABLE', desc: 'Enable geofence.' },
            { name: 'FENCE_TYPE', desc: 'Fence type bitmask (alt / circle / polygon).' },
            { name: 'FENCE_ACTION', desc: 'Action on breach.' },
            { name: 'FENCE_ALT_MAX', desc: 'Maximum altitude (m).' },
            { name: 'FENCE_ALT_MIN', desc: 'Minimum altitude (m).' },
            { name: 'FENCE_RADIUS', desc: 'Circular fence radius (m).' },
            { name: 'FENCE_MARGIN', desc: 'Margin from fence boundary (m).' },
        ],
    },
    {
        title: 'Return to Launch',
        rows: [
            { name: 'RTL_ALT', desc: 'RTL altitude (cm).' },
            { name: 'RTL_CLIMB_MIN', desc: 'Minimum climb before RTL (cm).' },
            { name: 'RTL_LOIT_TIME', desc: 'Loiter time at home before landing (ms).' },
            { name: 'RTL_ALT_FINAL', desc: 'Final RTL altitude (cm). -1 = land.' },
            { name: 'RTL_AUTOLAND', desc: 'Auto-land sequence at end of RTL (Plane).' },
        ],
    },
];

const Section = ({ title, rows, mavId, disabled, onCommit, theme, paramListTimestamp }) => {
    const visible = rows.filter((r) => !!getParamEntry(mavId, r.name));
    if (visible.length === 0) return null;
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography
                variant="caption"
                sx={{
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    display: 'block',
                    px: 1,
                    py: 0.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    mb: 0.5,
                }}
            >
                {title} <span style={{ opacity: 0.6 }}>({visible.length})</span>
            </Typography>
            <Box sx={{ px: 1 }}>
                {visible.map((r) => (
                    <ParamRow key={r.name} mavId={mavId} name={r.name} desc={r.desc} disabled={disabled} onCommit={onCommit} paramListTimestamp={paramListTimestamp} />
                ))}
            </Box>
        </Box>
    );
};

const Safety = ({ selectedMavId, paramListTimestamp }) => {
    const theme = useTheme();
    const [editEnabled, setEditEnabled] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const handleCommit = useCallback((name, value, type) => {
        if (!selectedMavId) return;
        window.api.setParams(selectedMavId, {
            [name]: { value, type },
        });
    }, [selectedMavId]);

    // Cheap counters for the header chips.
    const allDefined = SECTIONS.reduce((acc, s) => acc + s.rows.filter((r) => !!getParamEntry(selectedMavId, r.name)).length, 0);
    const totalKnown = SECTIONS.reduce((acc, s) => acc + s.rows.length, 0);

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                p: '2px',
                boxSizing: 'border-box',
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    overflow: 'hidden',
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <SafetyIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    <Typography variant="subtitle2">Safety Parameters</Typography>
                    <Chip
                        size="small"
                        label={`${allDefined} / ${totalKnown} available`}
                        color={allDefined > 0 ? 'primary' : 'default'}
                        sx={{ height: 20, fontSize: 11 }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={editEnabled}
                                onChange={(e) => {
                                    if (e.target.checked) setConfirmOpen(true);
                                    else setEditEnabled(false);
                                }}
                            />
                        }
                        label={<Typography variant="caption">Enable edit</Typography>}
                        sx={{ mr: 0 }}
                    />
                </Stack>

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        bgcolor: 'background.default',
                        p: 1,
                    }}
                >
                    {!selectedMavId && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            No vehicle selected.
                        </Typography>
                    )}
                    {selectedMavId && allDefined === 0 && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Waiting for parameters…
                        </Typography>
                    )}
                    {selectedMavId && SECTIONS.map((s) => (
                        <Section
                            key={s.title}
                            title={s.title}
                            rows={s.rows}
                            mavId={selectedMavId}
                            disabled={!editEnabled}
                            onCommit={handleCommit}
                            theme={theme}
                            paramListTimestamp={paramListTimestamp}
                        />
                    ))}
                </Box>
            </Box>

            <ConfirmationDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={() => { setEditEnabled(true); setConfirmOpen(false); }}
                title="Enable safety parameter editing?"
                description="Modifying failsafe, arming, fence or RTL parameters while the vehicle is armed or in flight can cause unexpected behavior and crash. Only proceed if the vehicle is safely on the ground."
                confirmColor="warning"
            />
        </Box>
    );
};

export default Safety;
